import axios from 'axios';
import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useFileDropZone } from '@/hooks/use-file-drop-zone';
import { useUploadProductionDeclarationDocuments } from '@/services/apis/mutations/production-declaration/use-upload-production-declaration-documents';

type UseProductionDeclarationMaterialAdditionalFilesDialogParams = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productionDeclarationId: string;
  materialId: string;
  commodityOriginId: string;
  token?: string;
};

export function useProductionDeclarationMaterialAdditionalFilesDialog({
  open,
  onOpenChange,
  productionDeclarationId,
  materialId,
  commodityOriginId,
  token,
}: UseProductionDeclarationMaterialAdditionalFilesDialogParams) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { mutateAsync: uploadDocument, isPending } =
    useUploadProductionDeclarationDocuments();

  const {
    dropZoneClassName,
    fileInputRef,
    handleDropZoneDragEnter,
    handleDropZoneDragLeave,
    handleDropZoneDragOver,
    handleDropZoneDrop,
    onFileInputChange,
    openFilePicker,
  } = useFileDropZone({
    open,
    onFileSelected: setSelectedFile,
  });

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [fileInputRef, open]);

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFileInputChange(event);
      event.target.value = '';
    },
    [onFileInputChange],
  );

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    try {
      await uploadDocument({
        commodityOriginId,
        file: selectedFile,
        materialId,
        productionDeclarationId,
        token,
      });

      toast.success(
        t(
          'materialCommodityOrigins.cards.additionalFiles.dialog.uploadSuccess',
        ),
      );
      onOpenChange(false);
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? (error.response?.data?.message ??
          error.response?.data?.detail ??
          t(
            'materialCommodityOrigins.cards.additionalFiles.dialog.uploadError',
          ))
        : t(
            'materialCommodityOrigins.cards.additionalFiles.dialog.uploadError',
          );

      toast.error(tCommon('error'), {
        description,
      });
    }
  }, [
    commodityOriginId,
    materialId,
    onOpenChange,
    productionDeclarationId,
    selectedFile,
    t,
    tCommon,
    token,
    uploadDocument,
  ]);

  return {
    dropZoneClassName,
    fileInputRef,
    handleCancel,
    handleDropZoneDragEnter,
    handleDropZoneDragLeave,
    handleDropZoneDragOver,
    handleDropZoneDrop,
    handleFileInputChange,
    handleSubmit,
    isPending,
    openFilePicker,
    selectedFile,
    t,
    tCommon,
  };
}
